import { escapeHTML } from '../../utils/escape.js';
import { formatFechaCorta } from '../../utils/formatters.js';

/**
 * Admin "Búsquedas" sub-section (openspec/changes/staff-faq/spec.md B6 and its
 * UI brief): failed staff searches grouped by normalized query. Pure render;
 * `js/faq.js` owns state and listeners.
 */

const TIPO_LABEL = { sin_resultado: 'Sin resultado', no_encontre: 'No encontré' };

/**
 * @param {{
 *   uiState: { status: 'loading' | 'error' | 'ready', mensaje?: string },
 *   grupos: Array<{ clave: string, consulta: string, tipos: string[], cantidad: number, ultima: string }>,
 *   truncado: boolean,
 *   marcando?: string | null,
 * }} props
 * @returns {string}
 */
export function FaqBusquedasView({ uiState, grupos, truncado, marcando = null }) {
    let cuerpo;
    if (uiState.status === 'loading') {
        cuerpo = `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="status" aria-live="polite">
                <i class="fa-solid fa-circle-notch fa-spin text-2xl text-brand mb-4" aria-hidden="true"></i>
                <p class="text-xs font-black text-text-muted uppercase tracking-widest">Cargando búsquedas...</p>
            </div>`;
    } else if (uiState.status === 'error') {
        cuerpo = `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="alert">
                <p class="text-sm font-bold text-text-primary mb-6">${escapeHTML(uiState.mensaje ?? 'No se pudieron cargar las búsquedas.')}</p>
                <button type="button" data-action="faq-reintentar" class="px-6 py-3 rounded-2xl bg-brand text-white text-xs font-black uppercase tracking-widest shadow-lg">Reintentar</button>
            </div>`;
    } else if (grupos.length === 0) {
        cuerpo = '<p class="py-12 text-center text-sm text-text-muted">No hay búsquedas pendientes.</p>';
    } else {
        const filas = grupos
            .map((g) => {
                const tipos = g.tipos
                    .map(
                        (t) =>
                            `<span class="px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10 text-[10px] font-black uppercase tracking-widest text-text-muted">${escapeHTML(TIPO_LABEL[t] ?? t)}</span>`,
                    )
                    .join('');
                const ocupado = marcando === g.clave;
                return `
                    <div data-faq-busqueda-grupo="${escapeHTML(g.clave)}" class="flex flex-wrap items-center justify-between gap-4 py-4 border-b border-border-subtle last:border-b-0">
                        <div class="flex items-center gap-4 min-w-0">
                            <span data-faq-busqueda-count aria-label="${g.cantidad} veces"
                                class="w-10 h-10 shrink-0 rounded-xl border-2 border-brand text-brand flex items-center justify-center text-sm font-black tabular-nums">${g.cantidad}</span>
                            <div class="min-w-0">
                                <p class="text-base font-bold text-text-primary break-words">“${escapeHTML(g.consulta)}”</p>
                                <div class="mt-1 flex flex-wrap items-center gap-2">
                                    ${tipos}
                                    <span class="text-[11px] text-text-muted">Última: ${escapeHTML(formatFechaCorta(g.ultima))}</span>
                                </div>
                            </div>
                        </div>
                        <button type="button" data-action="faq-marcar-revisada" data-clave="${escapeHTML(g.clave)}" ${ocupado ? 'disabled' : ''}
                            class="shrink-0 px-4 py-2 rounded-xl border border-border-subtle text-xs font-black uppercase tracking-widest text-text-muted hover:text-brand transition-colors disabled:opacity-60">
                            ${ocupado ? 'Marcando...' : 'Marcar revisada'}
                        </button>
                    </div>`;
            })
            .join('');
        cuerpo = `
            ${truncado ? '<p class="mb-3 text-xs font-bold text-amber-700 dark:text-amber-300">Se muestran las 1000 más recientes.</p>' : ''}
            <div class="bg-surface border border-border-subtle rounded-2xl px-5">${filas}</div>`;
    }

    return `
        <section data-faq-busquedas>
            <header class="mb-6">
                <h2 class="text-2xl font-black uppercase tracking-tight text-text-primary">Búsquedas sin respuesta</h2>
                <p class="mt-1 text-sm text-text-muted max-w-2xl">Lo que el staff buscó y no encontró. Agregá estas palabras a "Otras formas de preguntarlo" de la pregunta que corresponda, o creá una nueva.</p>
            </header>
            ${cuerpo}
        </section>
    `;
}
